import {useCurrentCompanyId} from "@/api/companyApi";
import {
  invalidateTiktokVisibilityQueries,
  tiktokCountryScanStreamKey,
  useTiktokCountryScanStateQuery,
  type TTiktokCountryScanProgress,
  type TTiktokCountryScanVariant,
} from "@/api/tiktokVisibilityApi";
import {USER_KEYS} from "@/api/userApi";
import TextButton from "@/components/Common/TextButton";
import {useSSE} from "@/hooks/useSSE";
import {useAgencyId, useIsAgency, useUserId} from "@/hooks/useUserHooks";
import {queryClient} from "@/queryClient";
import {
  tiktokCountryAddSessionAtom,
  tiktokCountrySwitchSessionAtom,
  tiktokVisibilitySelectedCountryAtom,
} from "@/store/jotaiAtoms";
import {useLocation, useNavigate} from "@tanstack/react-router";
import {useAtom, useSetAtom} from "jotai";
import {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {usePrevious} from "react-use";

const VARIANT_CONFIG = {
  add: {
    completedKey: "add_country_completed",
    failedKey: "add_country_failed",
    sessionAtom: tiktokCountryAddSessionAtom,
  },
  switch: {
    completedKey: "country_switch_completed",
    failedKey: "country_switch_failed",
    sessionAtom: tiktokCountrySwitchSessionAtom,
  },
} as const;

// Owns the single country-scan SSE connection app-wide so the scan keeps progressing while the user
// navigates away from the visibility tab, and the completion notice fires regardless of route. On a
// fresh load it asks the backend whether a scan is still running and reconnects to it. Covers both
// the "switch" and "add" flows — they differ only in which atom/state endpoint/toast copy they use.
export const useTiktokCountryScan = ({enabled, variant}: {enabled: boolean; variant: TTiktokCountryScanVariant}) => {
  const {completedKey, failedKey, sessionAtom} = VARIANT_CONFIG[variant];
  const {t} = useTranslation("search_visibility");
  const companyProfileId = useCurrentCompanyId();
  const isAgency = useIsAgency();
  const agencyId = useAgencyId();
  const navigate = useNavigate();
  const userId = useUserId();
  const {pathname} = useLocation();
  const [session, setSession] = useAtom(sessionAtom);
  const setSelectedCountry = useSetAtom(tiktokVisibilitySelectedCountryAtom);
  const previousCompanyProfileId = usePrevious(companyProfileId);
  useEffect(() => {
    if (previousCompanyProfileId == null || previousCompanyProfileId === companyProfileId) return;

    setSession(null);
  }, [companyProfileId, previousCompanyProfileId, setSession]);

  const {data: scanState} = useTiktokCountryScanStateQuery({
    companyProfileId,
    enabled: enabled && !!companyProfileId && !session,
    variant,
  });

  useEffect(() => {
    if (session || !scanState?.active || scanState.done || scanState.error || !scanState.session_id) return;

    setSession({
      initialProgress: scanState.progress,
      initialStep: scanState.current_step,
      streamId: scanState.session_id,
      targetCountry: scanState.country_code,
    });
  }, [session, scanState, setSession]);

  useEffect(() => {
    if (!session || !isAgency || !agencyId || session.agencyId === agencyId) return;

    setSession(prev => (prev ? {...prev, agencyId} : prev));
  }, [agencyId, isAgency, session, setSession]);

  useSSE<TTiktokCountryScanProgress>({
    enabled: enabled && !!session,
    initialState: {
      country_code: session?.targetCountry,
      current_step: session?.initialStep ?? "discovering_keywords",
      progress: session?.initialProgress ?? 0,
    },
    onError: () => {
      toast.error(t(failedKey));
      setSession(null);
    },
    onSuccess: () => {
      invalidateTiktokVisibilityQueries();

      if (userId) {
        [USER_KEYS.qUserNotificationCounts(userId), USER_KEYS.qUserNotificationList(userId)].map(queryKey =>
          queryClient.invalidateQueries({queryKey, refetchType: "all"}),
        );
      }

      if (companyProfileId && session?.targetCountry) {
        const targetCountry = session.targetCountry;
        setSelectedCountry(prev => ({...prev, [companyProfileId]: targetCountry}));
      }

      if (!pathname.includes("search-visibility")) {
        toast(
          <div className="flex items-center justify-between gap-3">
            <span>{t(completedKey)}</span>
            <TextButton
              className="shrink-0 text-sm"
              onClick={() =>
                navigate(
                  session?.agencyId
                    ? {
                        to: "/agency/$agencyId/subbrand/$companyId/search-visibility",
                        params: {agencyId: session?.agencyId!, companyId: companyProfileId!},
                        search: {tab: "overview"},
                      }
                    : {
                        to: "/company/$companyId/search-visibility",
                        params: {companyId: companyProfileId!},
                        search: {tab: "overview"},
                      },
                )
              }
              text={t("country_switch_view_results")}
            />
          </div>,
          {autoClose: false},
        );
      }

      setSession(null);
    },
    queryKey: session ? tiktokCountryScanStreamKey(variant, session.streamId) : [`__${variant}_stream_disabled__`],
  });
};
